import { useMemo } from "react";
import ReactFlow, { Background, Controls, ReactFlowProvider } from "reactflow";
import "reactflow/dist/style.css";
import * as Icons from "lucide-react";
import { SIMPLE_CATEGORIES, SIMPLE_CATEGORY_COLORS } from "@/constants/archimate";
import { SimpleNode } from "./SimpleNode";

const NODE_TYPES = { simple: SimpleNode };
const NODE_WIDTH = 190;
const NODE_HEIGHT = 64;

function CategoryLegend() {
  return (
    <div
      data-testid="simple-view-legend"
      className="bg-white/95 backdrop-blur border border-slate-200 rounded-lg shadow-md p-2 space-y-0.5"
    >
      <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-500 px-1.5 pb-1">Categories</div>
      {SIMPLE_CATEGORIES.map((cat) => {
        const colors = SIMPLE_CATEGORY_COLORS[cat];
        const Icon = Icons[colors.icon] || Icons.Circle;
        return (
          <div key={cat} className="flex items-center gap-2 px-1.5 py-1 text-xs text-slate-700">
            <span className="h-2.5 w-2.5 rounded-full border border-white shadow-sm flex-shrink-0" style={{ background: colors.border }} />
            <Icon size={12} style={{ color: colors.text }} />
            <span>{cat}</span>
          </div>
        );
      })}
    </div>
  );
}

function SimpleView({ data }) {
  const nodes = useMemo(
    () =>
      (data?.nodes || []).map((n) => ({
        id: n.id,
        type: "simple",
        position: { x: n.x, y: n.y },
        style: { width: NODE_WIDTH, height: NODE_HEIGHT },
        draggable: false,
        selectable: false,
        data: { name: n.name, category: n.category },
      })),
    [data]
  );

  const edges = useMemo(
    () =>
      (data?.edges || []).map((e, i) => ({
        id: `simple-edge-${i}`,
        source: e.source,
        target: e.target,
        type: "straight",
        style: { stroke: "#94A3B8", strokeWidth: 1.5 },
      })),
    [data]
  );

  return (
    <div className="relative h-full w-full bg-slate-50" data-testid="simple-view-canvas">
      <div className="absolute top-3 left-3 z-10">
        <CategoryLegend />
      </div>
      <div
        data-testid="simple-view-hint-text"
        className="absolute bottom-4 left-3 z-10 bg-white/90 backdrop-blur border border-slate-200 rounded-md px-2.5 py-1.5 text-[10px] text-slate-500"
      >
        Read-only overview - auto-arranged by category, no ArchiMate knowledge needed
      </div>
      <ReactFlow
        nodes={nodes}
        edges={edges}
        nodeTypes={NODE_TYPES}
        nodesDraggable={false}
        nodesConnectable={false}
        elementsSelectable={false}
        panOnDrag
        zoomOnScroll
        fitView
        minZoom={0.15}
        maxZoom={2}
        proOptions={{ hideAttribution: true }}
      >
        <Background variant="dots" gap={20} size={1} color="#CBD5E1" />
        <Controls showInteractive={false} />
      </ReactFlow>
    </div>
  );
}

export function SimpleViewWrapper({ data }) {
  return (
    <ReactFlowProvider>
      <SimpleView data={data} />
    </ReactFlowProvider>
  );
}
